/**
 * Rewards / incentives (web) — Phase 5 (#4 web surface), farmer side.
 *
 * A farmer's points wallet: a balance card, the points ledger (newest first), an
 * "earn more" tips block, and a redemption affordance that is DISABLED until
 * mainnet launch.
 *
 * The /incentives module is NEW and orval has NOT been regenerated for it, so this
 * screen uses the hand-rolled client src/api/incentives.ts (fetch-wrapper pattern,
 * same as src/api/buyers.ts) rather than generated hooks. Contract:
 *   GET  /incentives/me/balance              → { balance }   (integer points; default 0)
 *   GET  /incentives/me/ledger?limit&offset  → { data: [...] } (enveloped, newest first)
 *   POST /incentives/redeem                   → HTTP 501 (redemption opens at mainnet)
 *
 * POINTS are plain integers (NOT micro-ZARP) — no money conversion here. i18n keys
 * live under incentives.* in all four web locales. The nav item is role-gated to
 * `farmer` in _app.tsx (central wiring).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Icon,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import {
  getIncentiveBalance,
  getIncentiveLedger,
  type IncentiveKind,
  type IncentiveLedgerEntry,
} from '../api/incentives';

const LEDGER_LIMIT = 50;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dateFormatter.format(d);
}

/** Humanized label key for a ledger kind. */
function kindLabelKey(kind: IncentiveKind): string {
  switch (kind) {
    case 'data_quality':
      return 'incentives.kind.dataQuality';
    case 'participation':
      return 'incentives.kind.participation';
    case 'referral':
      return 'incentives.kind.referral';
    case 'redemption':
      return 'incentives.kind.redemption';
  }
}

/** Sign-prefixed points amount, e.g. "+10" / "-20". */
function formatPoints(amount: number): string {
  return amount > 0 ? `+${amount}` : String(amount);
}

export function IncentivesScreen() {
  const { t } = useTranslation();

  const balanceQuery = useQuery({
    queryKey: ['incentives-balance'],
    queryFn: ({ signal }) => getIncentiveBalance(signal),
    retry: false,
  });

  const ledgerQuery = useQuery({
    queryKey: ['incentives-ledger', { limit: LEDGER_LIMIT, offset: 0 }],
    queryFn: ({ signal }) => getIncentiveLedger({ limit: LEDGER_LIMIT, offset: 0 }, signal),
    retry: false,
  });

  // Balance defaults to 0 for a brand-new account (the contract guarantees a 0,
  // but a transient error should not show a misleading 0 — we gate on the query).
  const balance = balanceQuery.data?.balance ?? 0;
  const ledger = ledgerQuery.data?.data ?? [];

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('incentives.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('incentives.subtitle')}
          </Text>
        </Box>
      </Box>

      {/* Balance card. */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Text variant="label-medium" color="text.secondary">
                  {t('incentives.balance.label')}
                </Text>
                <Box sx={{ mt: 0.25 }}>
                  {balanceQuery.isPending ? (
                    <CircularProgress size={24} />
                  ) : balanceQuery.isError ? (
                    <Text variant="title-medium" color="text.secondary">
                      {t('incentives.balance.unavailable')}
                    </Text>
                  ) : (
                    <Text variant="display-small" as="span">
                      {t('incentives.balance.points', { count: balance })}
                    </Text>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {/* Redemption opens at mainnet — disabled with an explanatory note.
                    We do NOT call POST /incentives/redeem (it returns 501); the
                    button is purely a forward-looking affordance. */}
                <Button
                  variant="outlined"
                  disabled
                  startIcon={<Icon name="volunteer_activism" size={18} />}
                >
                  {t('incentives.redeem.disabled')}
                </Button>
              </Box>
            </Box>
            <Alert severity="info">{t('incentives.redeem.note')}</Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* Earn-more tips. */}
      <Card>
        <CardHeader title={t('incentives.tips.title')} />
        <CardContent>
          <Stack spacing={1.5}>
            <TipRow icon="verified" text={t('incentives.tips.dataQuality')} />
            <TipRow icon="fact_check" text={t('incentives.tips.traceEvent')} />
            <TipRow icon="payments" text={t('incentives.tips.settledTx')} />
          </Stack>
        </CardContent>
      </Card>

      {/* Ledger. */}
      <Box>
        <Text variant="title-large" as="h3">
          {t('incentives.ledger.title')}
        </Text>
        <Box sx={{ mt: 1.5 }}>
          {ledgerQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : ledgerQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => ledgerQuery.refetch()}>
                  {t('incentives.retry')}
                </Button>
              }
            >
              {t('incentives.ledger.loadError', {
                error:
                  ledgerQuery.error instanceof FseApiError
                    ? ledgerQuery.error.message
                    : String(ledgerQuery.error),
              })}
            </Alert>
          ) : ledger.length === 0 ? (
            <Card>
              <CardContent>
                <Stack spacing={1}>
                  <Text variant="title-medium" as="h4">
                    {t('incentives.ledger.empty.title')}
                  </Text>
                  <Text variant="body-medium" color="text.secondary">
                    {t('incentives.ledger.empty.body')}
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <LedgerTable rows={ledger} t={t} />
          )}
        </Box>
      </Box>
    </Stack>
  );
}

function TipRow({ icon, text }: { icon: string; text: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex' }}>
        <Icon name={icon} size={20} />
      </Box>
      <Text variant="body-medium" color="text.primary">
        {text}
      </Text>
    </Box>
  );
}

interface LedgerTableProps {
  rows: IncentiveLedgerEntry[];
  t: TFunction;
}

function LedgerTable({ rows, t }: LedgerTableProps) {
  return (
    <TableContainer component={Card}>
      <Table size="small" aria-label={t('incentives.ledger.aria')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('incentives.ledger.col.kind')}</TableCell>
            <TableCell align="right">{t('incentives.ledger.col.amount')}</TableCell>
            <TableCell>{t('incentives.ledger.col.reason')}</TableCell>
            <TableCell>{t('incentives.ledger.col.date')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const earned = row.amountPoints >= 0;
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Chip kind="assist" color="neutral" label={t(kindLabelKey(row.kind))} />
                </TableCell>
                <TableCell align="right">
                  <Text
                    variant="title-medium"
                    as="span"
                    color={earned ? 'primary.main' : 'error.main'}
                  >
                    {formatPoints(row.amountPoints)}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text variant="body-medium" color="text.secondary">
                    {row.reasonRef ?? '—'}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text variant="body-medium" color="text.secondary">
                    {formatDate(row.createdAt)}
                  </Text>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
